import * as Sentry from "@sentry/react";
import { sessionSlice } from "@store/session";
import { store } from "@store/store";
import type { Role, User } from "@types";
import axios from "axios";
import { redirect } from "react-router";
import { permissionsForUser } from "@/auth/permissions";
import { attachSilentAuthRetry } from "@/auth/silentReauth";
import {
  buildCompanyPath,
  stripBasename,
  stripCompanyPrefix,
} from "@/hooks/companyPath";
import { revokeRegisteredDispatchPushDevice } from "@/mobile/pushNotifications";
import { attachApiErrorReporting } from "@/services/apiErrorReporting";
import { attachAuthOriginGuard } from "@/services/authOriginGuard";
// Direct module import (not the @services barrel): the barrel pulls in the whole
// service surface; the loader needs only the id→slug company query factory.
import { companyQueryOptions } from "@/services/useCompaniesById";
import { getApiUrl, isNgrokUrl } from "@/utils/apiUrl";
import { shouldAddNgrokSkipBrowserWarning } from "@/utils/ngrokHeader";
import { safeConsole } from "@/utils/safeConsole";
import { queryClient } from "../queryClient";
import {
  canAccessRoute,
  findMatchingRoute,
  isPublicRoute,
} from "./routeConfig";
import { isSlugUnresolvable } from "./slugResolutionState";
import { SUPER_PATHS } from "./superRoutes";

attachAuthOriginGuard(axios);

const clearStoredSession = (): void => {
  void revokeRegisteredDispatchPushDevice();
  store.dispatch(sessionSlice.actions.logout());
  localStorage.removeItem("accessToken");
  localStorage.removeItem("expiration");
};

/**
 * Centralized auth check function
 * Used by both loader and axios interceptor
 */
export const checkAuth = (): boolean => {
  const accessToken = localStorage.getItem("accessToken");
  const expiration = Number(localStorage.getItem("expiration") || "");
  const currentTime = Math.floor(Date.now() / 1000);

  if (!accessToken || !expiration || expiration <= currentTime) {
    clearStoredSession();
    return false;
  }
  return true;
};

/**
 * Safely parse JSON from localStorage
 * Returns undefined if parsing fails (corrupted data, manual edits, etc.)
 */
const safeParseUser = (jsonString: string | null): User | undefined => {
  if (!jsonString) return undefined;
  try {
    return JSON.parse(jsonString) as User;
  } catch (error) {
    // Corrupted localStorage data - clear it and treat as logged out
    safeConsole.error(
      "authentication-lifecycle",
      "Failed to parse user from localStorage, clearing corrupted data",
      error,
    );
    localStorage.removeItem("user");
    return undefined;
  }
};

/**
 * Check if user has access to the selected company
 *
 * Uses localStorage as single source of truth for:
 * - Page refresh (Redux empty, localStorage populated)
 * - Multi-tab scenarios (Tab A changes company, Tab B needs to see it)
 * - Corrupted data handling (cleared localStorage = logged out)
 */
const checkCompanyAccess = (): boolean => {
  const companyId = localStorage.getItem("companyId") ?? null;
  const user = safeParseUser(localStorage.getItem("user"));

  // No user in localStorage = not authenticated
  if (!user) {
    // Clear orphaned companyId if user data is invalid/corrupted
    if (companyId !== null) localStorage.removeItem("companyId");
    return false;
  }

  if (!companyId) return false;

  // Superusers and owners can access any company on the frontend
  // (owners are limited by their account's companies in the company selector,
  // and the backend validates via check_company_access in dale/routers/__init__.py)
  if (user.role === "super" || user.role === "owner") return true;

  // For other roles, check companyIds array
  return user.companyIds?.includes(companyId) ?? false;
};

/**
 * Valid roles for runtime validation (prevents localStorage tampering).
 *
 * Restricted operational roles are valid *authenticated* roles so these users load and
 * render (e.g. in Team), but they are NOT broadly route-admitted:
 * `canAccessRoute` fail-closes each to an explicit allowlist rather than the
 * "no roles = all authenticated users" default. Route scoping lives there.
 */
const validRoles = new Set<Role>([
  "partner",
  "super",
  "owner",
  "admin",
  "member",
  "coach_csr",
  "rep",
  "dispatcher",
  "technician",
]);

/**
 * Validate a parsed user's role (prevents privilege escalation via localStorage
 * tampering like role: "superadmin").
 *
 * Returns undefined if the role is missing or invalid. Takes an already-parsed
 * user so the loader reads/parses localStorage once and derives both role and
 * permissions from the same blob.
 */
const validatedRole = (user?: User): Role | undefined => {
  const role = user?.role;

  // Runtime validation - reject invalid roles from corrupted/tampered localStorage
  if (role && !validRoles.has(role)) {
    Sentry.captureMessage(`Invalid role in localStorage: ${role}`, "warning");
    return undefined;
  }
  return role;
};

// Axios request interceptor for adding ngrok headers only to the Dale API host
axios.interceptors.request.use(
  (config) => {
    if (
      shouldAddNgrokSkipBrowserWarning({
        isNgrokUrl: isNgrokUrl(),
        requestUrl: config.url,
        baseUrl: config.baseURL,
        apiBaseUrl: getApiUrl(),
      })
    ) {
      config.headers["ngrok-skip-browser-warning"] = "true";
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Fallback when a 401 could not be recovered by silent re-auth. Shared by every
 * axios instance that carries the silent-auth retry interceptor.
 *
 * On an app page: clear an expired session and bounce to /login, preserving
 * the location for post-login redirect.
 *
 * On a public page (an invitation, a password reset, a buyer's quote, /login
 * itself): drop the session — expired or not, the API just refused it — and
 * stay put. These pages need no session, and bouncing them stranded invitees
 * whose browser still held a revoked session on a login form their pending
 * account cannot pass; checkAuth() keeps an unexpired token, so every retry
 * bounced again (SD-18605).
 */
export const redirectToLoginOnAuthFailure = (): void => {
  const { pathname, search, hash } = window.location;
  if (isPublicRoute(stripBasename(pathname))) {
    clearStoredSession();
    return;
  }
  checkAuth();
  window.location.href = `/login?redirectTo=${encodeURIComponent(`${pathname}${search}${hash}`)}`;
};

// 401 handling for the global axios: silent re-auth + replay, then redirect.
attachSilentAuthRetry(axios, redirectToLoginOnAuthFailure);
// After the retry so only unrecovered failures get logged/reported.
attachApiErrorReporting(axios);

/**
 * Route loader with multi-layer protection
 *
 * Validates:
 * 1. Authentication (token exists and not expired)
 * 2. Company access (for company-required routes)
 * 3. Role-based access (user role matches route requirements)
 *
 * Note: RBAC failures don't redirect - they return null and let
 * the layout show a "Forbidden" message for better UX.
 */
export const authLoader = async ({ request }: { request: Request }) => {
  const url = new URL(request.url);
  const companyPath = stripCompanyPrefix(stripBasename(url.pathname));
  const pathname = companyPath.pathname;
  const isCompanySlugRoute = companyPath.slug !== undefined;
  const redirectTarget = `${url.pathname}${url.search}${url.hash}`;

  // 1. Public routes don't need any checks
  if (!isCompanySlugRoute && isPublicRoute(pathname)) {
    return null;
  }

  // 2. Check authentication
  if (!checkAuth()) {
    return redirect(`/login?redirectTo=${encodeURIComponent(redirectTarget)}`);
  }

  // 3. Find the matching route config
  const route = findMatchingRoute(pathname);

  // Unknown routes - let the router's default 404 handling take over
  // or let the layout handle it (fail-closed at UI level)
  if (!route) {
    return null;
  }

  // Marketing Website used to be company-scoped. Preserve old bookmarks and
  // OAuth returns while moving them to its global Super URL.
  if (isCompanySlugRoute && pathname === SUPER_PATHS.marketingWebsite) {
    return redirect(SUPER_PATHS.marketingWebsite + url.search + url.hash);
  }

  // Global authenticated routes never carry tenant identity. Other
  // slug-scoped variants were short-lived, so reject them instead of
  // maintaining redirects.
  if (isCompanySlugRoute && route.access === "authenticated") {
    throw new Response("Not Found", { status: 404 });
  }

  // Parse the stored user ONCE, then derive both role and permissions from it.
  const storedUser = safeParseUser(localStorage.getItem("user"));
  const userRole = validatedRole(storedUser);
  if (userRole === "partner" && route.path !== "/partner-reporting")
    return redirect("/partner-reporting");

  // 4. Company scoping for company-required routes.
  //
  // The URL slug is the source of truth (SD-11900). A company-required route
  // reached UNPREFIXED — a bare bookmark, the post-login landing, an old deep
  // link — is redirected to its canonical bare-root `/:slug/...` form (SD-12120),
  // resolving the slug from the `localStorage.companyId` *hint*. Any slug-scoped
  // load skips this block (companySlugLoader owns those), so this fires at most
  // once per bare entry; the user then stays on slug URLs. localStorage.companyId
  // is now only a last-used hint, no longer the tenant source of truth.
  if (route.access === "company-required" && !isCompanySlugRoute) {
    if (!checkCompanyAccess()) {
      // No valid company hint. Superusers/owners pick one; regular users have a
      // server-side default company, so let the layout surface any error.
      if (userRole === "super") {
        return redirect(SUPER_PATHS.root);
      }
      if (userRole === "owner") {
        return redirect("/company-selection");
      }
      return null;
    }

    // checkCompanyAccess() passed, so a non-empty companyId hint is present.
    const hintCompanyId = localStorage.getItem("companyId") ?? "";
    const accessToken = localStorage.getItem("accessToken") ?? "";
    try {
      // Resolve the hint id → slug via the SAME `company(id).details()` cache the
      // shell/nav (useCompaniesById) and the slug committer prime, so this adds
      // no net request in steady state. `revalidateIfStale` refetches a stale
      // entry so we never redirect to an outdated slug the slug loader would then
      // reject. A slug-less company (older backend / null slug) or a resolve
      // error degrades to the legacy unprefixed render; buildCompanyPath never
      // emits `/undefined`.
      const company = await queryClient.ensureQueryData({
        ...companyQueryOptions(hintCompanyId, accessToken),
        revalidateIfStale: true,
      });
      // Loop-breaker: skip the redirect while this slug is marked unresolvable —
      // companySlugLoader just bounced off it, and re-slugifying would re-enter
      // the same failing `/:slug`. The mark clears on the slug's next successful
      // resolve, so this self-heals with nothing left in the URL (see
      // slugResolutionState).
      if (company?.slug && !isSlugUnresolvable(company.slug)) {
        return redirect(
          buildCompanyPath(company.slug, `${pathname}${url.search}${url.hash}`),
        );
      }
    } catch {
      // Transient id→slug resolve failure: fall through to the legacy render;
      // localStorage still scopes the tenant. Never redirect on error (avoids
      // a redirect loop when the backend is down).
    }
  }

  // 5. Check role-based access
  // Note: We don't redirect for RBAC failures - the layout will show "Forbidden"
  // This provides better UX than a redirect loop
  if (!canAccessRoute(route, userRole, permissionsForUser(storedUser))) {
    // Return null - layout's userIsAuthorized will show forbidden message
    return null;
  }

  return null;
};
